import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Linkedin, ExternalLink } from 'lucide-react';

import { useTenant } from '../../contexts/TenantContext';
import { PoweredByCerebroFooter } from '../PoweredByCerebro';

// Cerebro Logo - Teal/Gold Neural Brain Hexagon
const CEREBRO_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

// PDF Document URLs
const POLICY_URLS = {
  termsOfUse: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/dtzmhzj0_Terms%20of%20Use.pdf",
  privacyPolicy: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/s6j6bxyk_Privacy%20Policy.pdf",
  hitlGovernance: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/c94i8o5l_HITL%20Governance%20Policy.pdf",
  linkedin: "https://www.linkedin.com/company/navigate-compliance-group/"
};

export const Footer = () => {
  const { tenant } = useTenant();
  const currentYear = new Date().getFullYear();
  const isMaster = !tenant || tenant.isMaster;
  const orgName = isMaster ? 'Cerebro Core' : (tenant?.name || 'Cerebro Core');
  const logoUrl = (!isMaster && tenant?.branding?.logoUrl) ? tenant.branding.logoUrl : CEREBRO_LOGO;

  return (
    <footer 
      className="bg-navigate-dark border-t border-zinc-800"
      data-testid="main-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Brand Column */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img 
                src={logoUrl} 
                alt={orgName} 
                className="h-16 w-auto object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = CEREBRO_LOGO;
                }}
              />
              <div className="flex flex-col">
                <span className={`font-heading text-xl font-bold ${isMaster ? 'text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600' : 'text-white'}`}>
                  {orgName}
                </span>
                <span className="text-[10px] text-zinc-500 tracking-wider uppercase">
                  {isMaster ? 'A Workforce Capability Platform' : 'Learning Platform'}
                </span>
              </div>
            </Link>
            <p className="text-zinc-400 text-sm mb-4 max-w-md leading-relaxed">
              {isMaster 
                ? 'Cerebro Core is the workforce capability platform of the Navigate Compliance Group (Pty) Ltd - a South African governance & compliance technology firm. QCTO Accredited.'
                : `Official learning platform for ${orgName}.`
              }
            </p>
            {!isMaster && <PoweredByCerebroFooter className="!justify-start !py-0 mb-4" />}
            {isMaster && (
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <MapPin className="h-4 w-4" />
                Johannesburg, South Africa
              </div>
            )}
          </div>

          {/* Links Column */}
          <div className="md:text-right">
            {/* LinkedIn */}
            <a 
              href={POLICY_URLS.linkedin}
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors mb-6"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-5 w-5 text-zinc-400" />
              <span className="text-sm text-zinc-300">Navigate Compliance Group</span>
              <ExternalLink className="h-3 w-3 text-zinc-500" />
            </a>
            
            {/* Policy Links */}
            <div className="flex flex-wrap gap-4 md:justify-end text-sm mt-4">
              <Link 
                to="/faq"
                className="text-zinc-400 hover:text-navigate-gold transition-colors"
              >
                FAQ
              </Link>
              <a 
                href="/responsible-ai"
                className="flex items-center gap-1.5 text-zinc-400 hover:text-navigate-gold transition-colors"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                Responsible AI
              </a>
              <a 
                href={POLICY_URLS.privacyPolicy}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-navigate-gold transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href={POLICY_URLS.termsOfUse}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-navigate-gold transition-colors"
              >
                Terms of Use
              </a>
              <a 
                href={POLICY_URLS.hitlGovernance}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-navigate-gold transition-colors"
              >
                HITL Governance
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-zinc-800 text-center">
          <p className="text-sm text-zinc-500">
            © {currentYear} Navigate Compliance Group (Pty) Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
